/**
 * Epic Kanban View
 *
 * Uses the global Kanban component with epic-specific configuration.
 * All column definitions come from epicStatusConfig.ts
 */

import { useState, useEffect } from 'react';
import { Epic, EpicStatus, EpicProgress } from 'shared/types/epic';
import { EpicCard } from './EpicCard';
import { Kanban } from 'shared/components/Kanban';
import { epicsKanbanConfig } from 'shared/components/Kanban/configs/epicsKanbanConfig';
import { useSession } from '@clerk/clerk-react';
import { API_URL } from 'constants/index';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

interface EpicKanbanViewProps {
  epics: Epic[];
  onUpdate: () => void;
  onEpicClick?: (epic: Epic) => void;
  progressData?: Map<number, EpicProgress>;
}

export const EpicKanbanView = ({
  epics,
  onUpdate,
  onEpicClick,
  progressData
}: EpicKanbanViewProps) => {
  const { session } = useSession();

  // Local state for optimistic updates
  const [localEpics, setLocalEpics] = useState(epics);

  // Update local state when prop changes
  useEffect(() => {
    setLocalEpics(epics);
  }, [epics]);

  /**
   * Handle drop from the Kanban component
   */
  const handleDrop = async (
    epicId: string | number,
    newStatus: EpicStatus,
    oldStatus: EpicStatus
  ) => {
    // OPTIMISTIC UPDATE: Update local state immediately
    setLocalEpics((prev) =>
      prev.map((e) => (e.id === epicId ? { ...e, status: newStatus } : e))
    );

    // Make API call in background
    try {
      const response = await fetchWithAuth(
        `${API_URL}/epics/${epicId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus })
        },
        session
      );

      if (!response.ok) {
        throw new Error('Failed to update epic status');
      }

      toast.success(`Epic verplaatst naar "${newStatus}"`);
    } catch (error: any) {
      console.error('Error updating epic status:', error);
      Sentry.captureException(error, {
        tags: { component: 'EpicKanbanView' },
        extra: { epicId, newStatus, oldStatus }
      });
      toast.error(
        `Fout bij verplaatsen epic: ${error.message || 'Onbekende fout'}`
      );

      // ROLLBACK: Revert local state on error
      setLocalEpics((prev) =>
        prev.map((e) => (e.id === epicId ? { ...e, status: oldStatus } : e))
      );

      // Refetch to ensure data consistency
      onUpdate();
    }
  };

  /**
   * Render an epic card
   */
  const renderEpicCard = (epic: Epic) => {
    const progress = progressData?.get(epic.id);

    // Calculate progress from features if not provided
    const calculatedProgress = progress || {
      total: epic.features?.length || 0,
      completed: epic.features?.filter((f) => f.status === 'Gesloten').length || 0,
      reported: 0,
      inProgress: 0,
      testing: 0,
      resolved: 0,
      closed: 0,
      percentComplete: 0
    };

    return (
      <EpicCard
        epic={epic}
        onClick={() => onEpicClick?.(epic)}
        progress={calculatedProgress as { total: number; completed: number; percentComplete: number } | undefined}
      />
    );
  };

  return (
    <Kanban
      config={epicsKanbanConfig}
      items={localEpics}
      renderCard={renderEpicCard}
      onDrop={handleDrop}
      onCardClick={onEpicClick}
    />
  );
};
